import { findExecutiveUser, findSupervisorUser, type ApprovalResolverUser } from './purchasingApprovals';

/** Receipt total may differ from the PO by this much without extra approval. */
export const PURCHASE_ACTUAL_VARIANCE_LIMIT = 0.03;

export function roundPurchaseUsd(value: number): number {
  return Math.round(value * 100) / 100;
}

export function parsePurchaseUsdInput(raw: string): number | null {
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return roundPurchaseUsd(parsed);
}

export function purchaseActualVarianceRatio(poAmount: number, actualAmount: number): number {
  const po = Number(poAmount);
  const actual = Number(actualAmount);
  if (!Number.isFinite(po) || !Number.isFinite(actual)) return Number.POSITIVE_INFINITY;
  if (po === 0) return actual === 0 ? 0 : Number.POSITIVE_INFINITY;
  return Math.abs(actual - po) / po;
}

export function purchaseActualWithinTolerance(poAmount: number, actualAmount: number): boolean {
  if (!Number.isFinite(actualAmount) || actualAmount < 0) return false;
  return purchaseActualVarianceRatio(poAmount, actualAmount) <= PURCHASE_ACTUAL_VARIANCE_LIMIT + 1e-12;
}

export function formatPurchaseActualVariance(
  poAmount: number,
  actualAmount: number,
  mode: 'requester' | 'authority' = 'requester',
): string {
  const ratio = purchaseActualVarianceRatio(poAmount, actualAmount);
  if (!Number.isFinite(actualAmount) || actualAmount < 0) return 'Enter the receipt total.';
  if (!Number.isFinite(ratio)) {
    return mode === 'authority'
      ? 'This $0 PO has a receipt total. Confirm it as their authority.'
      : 'A $0 PO needs your supervisor if the receipt is not $0.';
  }
  const pct = (ratio * 100).toFixed(1);
  if (ratio <= 0.00005) return 'Matches the PO.';
  if (ratio <= PURCHASE_ACTUAL_VARIANCE_LIMIT + 1e-12) {
    return actualAmount >= poAmount ? `${pct}% over the PO (within 3%).` : `${pct}% under the PO (within 3%).`;
  }
  const direction = actualAmount >= poAmount ? 'over' : 'under';
  return mode === 'authority'
    ? `${pct}% ${direction} the PO. You can confirm this as their authority.`
    : `${pct}% ${direction} the PO — send to your supervisor.`;
}

export function resolvePurchaseActualAuthority(input: {
  actorId: string;
  users: ApprovalResolverUser[];
  fallbackApproverUserId?: string;
}): { userId: string; userName: string } | null {
  const supervisor = findSupervisorUser(input.users, input.actorId);
  if (supervisor?.uid) {
    return { userId: supervisor.uid, userName: (supervisor.displayName || '').trim() || 'Supervisor' };
  }
  const fallback = input.users.find(u => u.uid === input.fallbackApproverUserId && u.isActive !== false);
  if (fallback?.uid) {
    return { userId: fallback.uid, userName: (fallback.displayName || '').trim() || 'Approver' };
  }
  const exec = findExecutiveUser(input.users);
  if (exec?.uid && exec.uid !== input.actorId) {
    return { userId: exec.uid, userName: (exec.displayName || '').trim() || 'Executive' };
  }
  return null;
}

export function canSelfConfirmPurchaseActual(input: {
  canOverride: boolean;
  actorId: string;
  authorityUserId?: string | null;
  varianceReviewerId?: string | null;
}): boolean {
  if (input.canOverride) return true;
  if (input.varianceReviewerId && input.varianceReviewerId === input.actorId) return true;
  if (!input.authorityUserId) return true;
  return input.authorityUserId === input.actorId;
}
